// =============================================================================
// briefMerge.js — the Daily Brief's sections are Harvi's written items PLUS
// every unread thread already sorted as needing the person. Pure.
//
// WHY. The brief is one Harvi turn under a 20-call budget, so it lists the
// handful of threads it read this turn (5 on a 110-thread Inbox) while the
// stored verdicts — written by the background mail sync and the Sort button,
// under the same mail-triage rules — already know 33 need the person. The
// header said 33 and the sections showed 5. Harvi's own item wins where both
// cover a thread (it read it this turn); a sorted row is added only where the
// brief says nothing. FYI and "nothing needed" are NOT merged: they stay in
// All unread, or the brief becomes the whole Inbox.
// =============================================================================

export const MERGED_BUCKETS = ['today', 'week', 'awaiting'];

const ms = (v) => { const t = new Date(v || '').getTime(); return Number.isFinite(t) ? t : 0; };

// A thread whose sender the person has since written to in ANOTHER
// conversation (Outlook splits a chain when the subject changes) is flagged
// by the server as `repliedElsewhere`. The brief keeps it, but apart from the
// "needs you" sections — see unreadQueue.js markRepliedElsewhere. This copies
// the flag onto Harvi's own written items too, by message or conversation.
export function flagRepliedElsewhere(items, unreadRows) {
  const rows = Array.isArray(unreadRows) ? unreadRows : [];
  return (Array.isArray(items) ? items : []).map((i) => {
    if (!i || i.repliedElsewhere) return i;
    const row = rows.find(r => r && r.repliedElsewhere && (
      (r.messageIds || [r.messageId]).includes(i.messageId)
      || (i.conversationId && r.conversationId === i.conversationId)));
    return row ? { ...i, repliedElsewhere: row.repliedElsewhere } : i;
  });
}

// A deadline written as a date (2026-09-29) that is before today, local time.
export function deadlinePassed(deadline, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(deadline || '').trim());
  if (!m) return false;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return d < today;
}

// Harvi's written items are frozen into the brief turn, so a message read
// since (here, in the hover card, or in Outlook) kept showing until the next
// brief. Once the live unread listing is in, a written mail item whose
// message and thread are no longer unread is dropped. Only where the listing
// can answer: an item older than the oldest listed message, on a listing cut
// off at its cap, is kept — "not listed" there may mean "not reached".
export function dropReadItems(items, unread) {
  const list = Array.isArray(items) ? items : [];
  if (!unread || !unread.available || !Array.isArray(unread.rows)) return list;
  const ids = new Set();
  const convs = new Set();
  let oldest = Infinity;
  for (const r of unread.rows) {
    for (const id of (r.messageIds || [r.messageId])) if (id) ids.add(id);
    if (r.conversationId) convs.add(r.conversationId);
    const t = ms(r.receivedAt);
    if (t && t < oldest) oldest = t;
  }
  return list.filter((i) => {
    if (!i || !i.messageId) return true;
    if (ids.has(i.messageId)) return true;
    if (i.conversationId && convs.has(i.conversationId)) return true;
    if (unread.truncated && !(ms(i.receivedAt) > oldest)) return true;
    return false;
  });
}

export function mergeSortedMail(items, unreadRows) {
  const base = Array.isArray(items) ? items : [];
  const rows = Array.isArray(unreadRows) ? unreadRows : [];
  const ids = new Set(base.map(i => i && i.messageId).filter(Boolean));
  const convs = new Set(base.map(i => i && i.conversationId).filter(Boolean));
  const added = [];
  for (const r of rows) {
    if (!r || !MERGED_BUCKETS.includes(r.group)) continue;
    if (r.conversationId && convs.has(r.conversationId)) continue;
    if ((r.messageIds || [r.messageId]).some(id => ids.has(id))) continue;
    added.push({
      bucket: r.group,
      messageId: r.messageId,
      conversationId: r.conversationId,
      subject: r.subject || null,
      from: r.from || null,
      receivedAt: r.receivedAt || null,
      preview: r.preview || null,
      hasAttachments: !!r.hasAttachments,
      webLink: r.webLink || null,
      summary: r.summary || null,
      dealName: r.dealName || null,
      dealId: r.dealId || null,
      deadline: r.deadline || null,
      unreadCount: r.unreadCount || 1,
      verified: true,
      fromSort: true,
      repliedElsewhere: r.repliedElsewhere || null,
    });
    if (r.conversationId) convs.add(r.conversationId);
  }
  added.sort((a, b) => ms(b.receivedAt) - ms(a.receivedAt));
  return [...base, ...added];
}
